import { createApi } from '@reduxjs/toolkit/query/react';
import { dynamicBaseQuery } from '../badRequestHandler';

/* ─── Types ──────────────────────────────────────────────────────────── */

interface AddVendorArgs {
  name:       string;
  mobile:     string;
  categoryId: string;
}

interface UpdateVendorArgs extends Partial<AddVendorArgs> {
  id: string;
}

interface GetVendorsParams {
  search?: string;
  status?: 'all' | 'active' | 'inactive';
  page?:   number;
  limit?:  number;
}

interface GetVendorTransactionsParams {
  vendorId:     string;
  paidVia?:     'wallet' | 'outstanding';
  paymentType?: 'full' | 'partial';
  page?:        number;
  limit?:       number;
}

interface GetMerchantTransactionsParams {
  merchantId:   string;
  paidVia?:     'wallet' | 'outstanding';
  paymentType?: 'full' | 'partial';
  startDate?:   string;
  endDate?:     string;
  page?:        number;
  limit?:       number;
}

interface GetAllVendorTransactionsParams {
  startDate?: string;
  endDate?:   string;
  createdBy?: string;
  branchId?:  string;
  category?:  string;
  page?:      number;
  limit?:     number;
}

interface AddVendorTransactionArgs {
  vendor?:          string;
  merchantId?:      string;
  transactionDate?: string;
  amount:       number;
  paymentType:  'full' | 'partial';
  paidVia:      'wallet' | 'outstanding';
  paymentMode:  'cash' | 'online';
  paidAmount:   number;
  dueAmount:    number;
  bill?:        string;
  paymentBill?: string;
  description?: string;
  branchId?:    string;
  branchIds?:   string[];
}

interface UpdateVendorTransactionArgs extends Partial<Omit<AddVendorTransactionArgs, 'vendor'>> {
  id: string;
}

/* ─── API ────────────────────────────────────────────────────────────── */

export const vendorApi = createApi({
  reducerPath: 'vendorApi',
  baseQuery:   dynamicBaseQuery,
  tagTypes:    ['Vendor'] as const,
  endpoints:   (builder) => ({
    getVendors: builder.query<unknown, GetVendorsParams | void>({
      query: (params) => {
        const { search: searchTerm, status, page, limit } = params || {};
        const search = new URLSearchParams();
        if (searchTerm)      search.append('search', searchTerm);
        if (status)          search.append('status', status);
        if (page  != null)   search.append('page', String(page));
        if (limit != null)   search.append('limit', String(limit));
        const qs = search.toString();
        return { url: qs ? `/vendors?${qs}` : `/vendors`, method: 'GET' };
      },
      providesTags: ['Vendor'],
    }),
    addVendor: builder.mutation<unknown, AddVendorArgs>({
      query: (body) => ({ url: `/vendors`, method: 'POST', body }),
      invalidatesTags: ['Vendor'],
    }),
    updateVendor: builder.mutation<unknown, UpdateVendorArgs>({
      query: ({ id, ...body }) => ({ url: `/vendors/${id}`, method: 'PUT', body }),
      invalidatesTags: ['Vendor'],
    }),
    toggleVendorStatus: builder.mutation<unknown, { id: string; isActive: boolean }>({
      query: ({ id, isActive }) => ({ url: `/vendors/${id}/status`, method: 'PATCH', body: { isActive } }),
      invalidatesTags: ['Vendor'],
    }),
    getVendorTransactions: builder.query<unknown, GetVendorTransactionsParams>({
      query: ({ vendorId, paidVia, paymentType, page, limit }) => {
        const qp = new URLSearchParams();
        if (paidVia)         qp.append('paidVia', paidVia);
        if (paymentType)     qp.append('paymentType', paymentType);
        if (page  != null)   qp.append('page', String(page));
        if (limit != null)   qp.append('limit', String(limit));
        const qs = qp.toString();
        return { url: qs ? `/vendors/${vendorId}/transactions?${qs}` : `/vendors/${vendorId}/transactions`, method: 'GET' };
      },
      providesTags: ['Vendor'],
    }),
    getMerchantTransactions: builder.query<unknown, GetMerchantTransactionsParams>({
      query: ({ merchantId, paidVia, paymentType, startDate, endDate, page, limit }) => {
        const qp = new URLSearchParams();
        qp.append('merchantId', merchantId);
        if (paidVia)         qp.append('paidVia', paidVia);
        if (paymentType)     qp.append('paymentType', paymentType);
        if (startDate)       qp.append('startDate', startDate);
        if (endDate)         qp.append('endDate', endDate);
        if (page  != null)   qp.append('page', String(page));
        if (limit != null)   qp.append('limit', String(limit));
        return { url: `/vendors/transactions?${qp.toString()}`, method: 'GET' };
      },
      providesTags: ['Vendor'],
    }),
    addVendorTransaction: builder.mutation<unknown, AddVendorTransactionArgs>({
      query: (body) => ({ url: `/vendors/transactions`, method: 'POST', body }),
      invalidatesTags: ['Vendor'],
    }),
    updateVendorTransaction: builder.mutation<unknown, UpdateVendorTransactionArgs>({
      query: ({ id, ...body }) => ({ url: `/vendors/transactions/${id}`, method: 'PUT', body }),
      invalidatesTags: ['Vendor'],
    }),
    verifyVendorTransaction: builder.mutation<unknown, { id: string; isVerified: boolean }>({
      query: ({ id, isVerified }) => ({ url: `/vendors/transactions/${id}/verify`, method: 'PATCH', body: { isVerified } }),
      invalidatesTags: ['Vendor'],
    }),
    getAllVendorTransactions: builder.query<unknown, GetAllVendorTransactionsParams | void>({
      query: (params) => {
        const { startDate, endDate, createdBy, branchId, category, page, limit } = params || {};
        const qp = new URLSearchParams();
        if (startDate)       qp.append('startDate', startDate);
        if (endDate)         qp.append('endDate', endDate);
        if (createdBy)       qp.append('createdBy', createdBy);
        if (branchId)        qp.append('branchId', branchId);
        if (category)        qp.append('category', category);
        if (page  != null)   qp.append('page', String(page));
        if (limit != null)   qp.append('limit', String(limit));
        const qs = qp.toString();
        return { url: qs ? `/vendors/transactions?${qs}` : `/vendors/transactions`, method: 'GET' };
      },
      providesTags: ['Vendor'],
    }),
    getVendorCategories: builder.query<unknown, void>({
      query: () => ({ url: `/vendors/categories`, method: 'GET' }),
      providesTags: ['Vendor'],
    }),
    addVendorCategory: builder.mutation<unknown, { name: string }>({
      query: (body) => ({ url: `/vendors/categories`, method: 'POST', body }),
      invalidatesTags: ['Vendor'],
    }),
  }),
});

export const {
  useGetVendorsQuery,
  useAddVendorMutation,
  useUpdateVendorMutation,
  useToggleVendorStatusMutation,
  useGetVendorTransactionsQuery,
  useGetMerchantTransactionsQuery,
  useAddVendorTransactionMutation,
  useUpdateVendorTransactionMutation,
  useVerifyVendorTransactionMutation,
  useGetAllVendorTransactionsQuery,
  useGetVendorCategoriesQuery,
  useAddVendorCategoryMutation,
} = vendorApi;
